import { useState } from 'react'
import { getBezierPath, getEdgeCenter, Position } from 'react-flow-renderer'

import { NodeData, GraphNode } from './NodeData'

import styles from './ComponentEdgeEditMode.module.css'

const foreignObjectSize = 40

interface ComponentEdgeParams {
  id: string
  sourceX: number
  sourceY: number
  targetX: number
  targetY: number
  sourcePosition: Position
  targetPosition: Position
  style: any
  data: NodeData
}

/**
 * Component for rendering an edge between two nodes in the graph.
 *
 * @param {Object} params - The parameters for rendering the edge.
 * @param {string} params.id - The unique identifier of the edge.
 * @param {number} params.sourceX - The X-coordinate of the source node.
 * @param {number} params.sourceY - The Y-coordinate of the source node.
 * @param {number} params.targetX - The X-coordinate of the target node.
 * @param {number} params.targetY - The Y-coordinate of the target node.
 * @param {string} params.sourcePosition - The position of the source handle (e.g., 'right', 'left').
 * @param {string} params.targetPosition - The position of the target handle (e.g., 'top', 'bottom').
 * @param {Object} params.style - The CSS style for rendering the edge.
 * @param {Object} params.data - Additional data associated with the edge.
 * @param {GraphNode | null} selectedNode - The selected node.
 * @param {function} setSelectedNode - A function to set the selected node.
 * @returns {JSX.Element} The JSX element representing the `ComponentEdge` component.
 */
export default function ComponentEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  data,
}: ComponentEdgeParams) {
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null)
  data.setSelectedNode = setSelectedNode

  const edgePath = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })
  const [edgeCenterX, edgeCenterY] = getEdgeCenter({
    sourceX,
    sourceY,
    targetX,
    targetY,
  })

  return (
    <>
      <path id={id} style={style} className="react-flow__edge-path" d={edgePath} />
      {/* Note this is not currently used anywhere, but is how we would show
           extra buttons for selected node actions. */}
      {selectedNode ? (
        <foreignObject
          width={foreignObjectSize}
          height={foreignObjectSize}
          x={edgeCenterX - foreignObjectSize / 2}
          y={edgeCenterY - foreignObjectSize / 2}
          className={styles.edgeButton}
          requiredExtensions="http://www.w3.org/1999/xhtml"
        >
          <div>
            <button
              className="edgebutton"
              onClick={(event) => {
                event.stopPropagation()
              }}
            >
              ×
            </button>
          </div>
        </foreignObject>
      ) : null}
    </>
  )
}
